import { useContext, useEffect, useRef } from 'react'
import {
  GlobalDataContext,
  TemplateContext,
  View,
  evaluateTemplateValue,
  propsDataLocationToPathAndValue,
  useEvaluatedAttributes,
} from '@ea-lab/reactive-json'
import { registerUnit } from '../editable/unitRegistry'
import { BuilderContext } from '../BuilderContextInstance'
import { UnitDataBaseContext } from '../editable/unitDataBaseContext'
import { UnitChainContext } from './UnitMount'

/**
 * Résout un chemin du store DE L'UNITÉ (celui du subroot) en `{formData,
 * formDataPath}`. Hors composant : les closures enregistrées lisent le contexte
 * par la ref, donc elles ne périment jamais.
 */
const resolveStorePath = (ctxRef, dataLocation) => {
  const ctx = ctxRef.current
  return propsDataLocationToPathAndValue({
    currentPath: ctx.path,
    datafield: ctx.datafield,
    dataLocation,
    globalDataContext: ctx.globalDataContext,
    templateContext: ctx.templateContext,
  })
}

/**
 * BuilderUnit — marks an editable rjbuild unit and bridges its store to the
 * edit runtime.
 *
 * Mounted as the root element of an editable unit's rjBuild, it:
 *  - marks its DOM element with `data-builder-unit="<unitId>"` and
 *    `data-builder-unit-editable="true|false"` so the click runtime can resolve
 *    the owning unit via `closest('[data-builder-unit]')`;
 *  - captures the *owning* store's `updateData` (it lives inside the subroot,
 *    so `GlobalDataContext` here is that subroot's) and registers a
 *    `setFieldValue(field, value)` closure in `unitRegistry`;
 *  - resolves the editability gate from `~~.apiData.submission_url` — no URL ⇒
 *    Drupal denied write ⇒ the unit is read-only.
 *
 * The Syncable Object is expected under `~~.apiData` (`{item_url,
 * submission_url, delete_url, data}`), the kanban convention. Field edits
 * write `~~.apiData.data.<field>`.
 *
 * YAML usage:
 *   - type: BuilderUnit
 *     tag: div                 # optional, default div (display:contents)
 *     attributes: { class: '...' }
 *     content: [ ... unit body, with editable: nodes ... ]
 */
export const BuilderUnit = ({ props, path, currentData, datafield }) => {
  const globalDataContext = useContext(GlobalDataContext)
  const templateContext = useContext(TemplateContext)

  // Latest context kept in a ref so the registered closure never goes stale,
  // and so we don't re-register on every render.
  const ctxRef = useRef(null)
  ctxRef.current = { globalDataContext, templateContext, path, datafield }

  const resolve = (v) =>
    evaluateTemplateValue({ valueToEvaluate: v, globalDataContext, templateContext })

  const submissionUrl = resolve(props.submissionUrlLocation ?? '~~.apiData.submission_url')
  const hasSubmissionUrl = !!submissionUrl

  // The Syncable identity — same for every mount of the same entity. NOT the
  // unit id (two mounts of one entity must be distinct units, see below); it's
  // the *grouping* key for live cross-mount sync (units sharing an entityKey
  // mirror each other's edits).
  const itemUrl = resolve(props.unitIdLocation ?? '~~.apiData.item_url')
  const entityKey = typeof itemUrl === 'string' && itemUrl !== '' ? itemUrl : null

  // Editability gate. Inside a UnitMount subroot the rule is STRICT — editable
  // iff a submission_url is granted; a presentational template (file/no data)
  // is read-only. At page level the `!exists` leniency lets a freshly-added
  // block (no item_url yet) edit via the legacy page-state path until its first
  // save creates the entity.
  const inSubroot = useContext(UnitChainContext).length > 0
  const exists = entityKey !== null
  const isEditable = inSubroot ? hasSubmissionUrl : (hasSubmissionUrl || !exists)

  // Unit id: ALWAYS a per-mount uuid (kept in a ref, stable across renders).
  // Not the item_url — two mounts of the same entity would then collide in the
  // registry and edits would misroute to whichever registered last.
  const fallbackIdRef = useRef(null)
  if (fallbackIdRef.current == null) {
    fallbackIdRef.current =
      globalThis.crypto?.randomUUID?.() ?? `unit-${Math.random().toString(36).slice(2)}`
  }
  const unitId = fallbackIdRef.current

  // Base store path the editable fields live under. Defaults to the Syncable
  // convention `~~.apiData.data`; a block_content unit (whose decoupled shape
  // nests fields under objectData) sets `~~.apiData.data.objectData.data`. It's a
  // path prefix, not a value — used verbatim, never evaluated.
  const dataBase = props.dataBaseLocation ?? '~~.apiData.data'
  const dataBaseRef = useRef(dataBase)
  dataBaseRef.current = dataBase

  // Identité du bloc dans le canevas (`bloc.id`), portée par l'enveloppe que
  // synthétise `BuilderBlockMount`. C'est la clé par laquelle le save du canevas
  // retrouve le contenu vivant d'une unité qui ne se persiste pas elle-même.
  const builderIdRaw = resolve(props.builderIdLocation ?? '~~.apiData.data.objectData.builderId')
  const builderId = typeof builderIdRaw === 'string' && builderIdRaw !== '' ? builderIdRaw : null
  const builderIdRef = useRef(builderId)
  builderIdRef.current = builderId

  // Contexte du canevas — `null` hors builder (rendu autonome, tests) : tout ce
  // qui en dépend reste optionnel.
  const builderCtx = useContext(BuilderContext)

  useEffect(() => {
    const resolveField = (field) => resolveStorePath(ctxRef, `${dataBaseRef.current}.${field}`)
    const setFieldValue = (field, value) => {
      const { formDataPath } = resolveField(field)
      ctxRef.current.globalDataContext.updateData(value, formDataPath)
    }
    // Read bridge: the unit store is the live source of a unit's content
    // (bloc.data is only the seed). Editors read their initial value through
    // this so reopening a modal shows the latest edit, not the stale seed.
    const getFieldValue = (field) => resolveField(field).formData
    // Même pont, mais pour TOUT le contenu de l'unité : le save du canevas y
    // prend les valeurs qu'un champ de formulaire du bloc (les filtres d'un
    // widget de tableau de bord) écrit directement dans le store, sans jamais
    // passer par `commitField` — donc invisibles de `bloc.data`.
    const getData = () => resolveStorePath(ctxRef, dataBaseRef.current).formData
    return registerUnit(unitId, {
      hasSubmissionUrl,
      builderId: builderIdRef.current,
      setFieldValue,
      getFieldValue,
      getData,
    })
  }, [unitId, hasSubmissionUrl, builderId])

  // Une unité auto-persistée signale ses modifications par sa propre DataSync.
  // Celles que le canevas enregistre à leur place (aucun `submission_url` : les
  // blocs d'un tableau de bord, un bloc de page pas encore créé) n'ont AUCUN
  // signal quand un champ de formulaire écrit dans leur store — le bouton
  // « Enregistrer » resterait grisé et la sélection serait perdue.
  const tracksStoreEdits = !!builderCtx?.isEditMode && !hasSubmissionUrl && !!builderId
  const lastDataRef = useRef(null)
  // Sans tableau de dépendances : l'écriture dans le store ne se voit que par la
  // valeur résolue, donc l'effet doit passer à CHAQUE rendu de l'unité.
  useEffect(() => {
    if (!tracksStoreEdits) return
    const serialized = JSON.stringify(resolveStorePath(ctxRef, dataBaseRef.current).formData ?? null)
    // Premier passage = la référence (le seed), jamais une modification.
    if (lastDataRef.current === null) {
      lastDataRef.current = serialized
      return
    }
    if (serialized === lastDataRef.current) return
    lastDataRef.current = serialized
    builderCtx.setIsDirty(true)
  })

  const evaluatedAttrs = useEvaluatedAttributes(props.attributes)
  const { style: userStyle, ...restAttrs } = evaluatedAttrs
  const Tag = props.tag ?? 'div'

  return (
    <Tag
      {...restAttrs}
      data-builder-unit={unitId}
      data-builder-unit-editable={isEditable ? 'true' : 'false'}
      // Layout-transparent by default so wrapping a block doesn't introduce a
      // box (keeps position:sticky etc. working); author can override.
      style={{ display: 'contents', ...(userStyle ?? {}) }}
    >
      {props.content && (
        <UnitDataBaseContext.Provider value={dataBase}>
          <View
            props={props.content}
            path={path + '.content'}
            datafield="content"
            currentData={currentData?.content}
          />
        </UnitDataBaseContext.Provider>
      )}
    </Tag>
  )
}
